import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleMinus,
  faEllipsis,
  faPlus,
  faArrowDownShortWide,
  faArrowDownWideShort,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import InputField from "../../components/InputField";

import { useAuth } from "../../providers/AuthContext";
import { useTranslation } from "react-i18next";
import Dropdown from "../../components/Dropdown";
import { getChargingSiteThirdPartySystemMapping } from "../../api/ChargingSites";
import { getAllThirdPartySystems } from "../../api/Resources";
import toast from "react-hot-toast";
import ModalBase from "../../components/ModalBase";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomDropdown from "../../components/CustomDropdown";
import LoadingSpinner from "../../components/LoadingSpinner";

const SiteThirdPartyRef = ({
  setHasChanges = () => {},
  thirdPartySystemMapping,
  setThirdPartySystemMapping,
}) => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth();
  const { id } = useParams();
  const isAdmin = hasRole?.("Admin") ?? false;

  const [thirdPartySystems, setThirdPartySystems] = useState([]);
  const [localMappings, setLocalMappings] = useState(thirdPartySystemMapping || []);
  const [systemsLoading, setSystemsLoading] = useState(true);

  const [addModal, setAddModal] = useState(false);
  const [addTPRState, setAddTPRState] = useState({ system: "", id: "" });
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });

  useEffect(() => {
    if (id !== "0" && id) {
      handleGetChargingSiteThirdPartySystemMapping();
    } else {
    }
    if (isAdmin) {
      handleGetAllThirdPartySystems();
    } else {
      setThirdPartySystems([]);
      setSystemsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [id, isAdmin]);

  const handleGetChargingSiteThirdPartySystemMapping = async () => {
    try {
      const accessToken = await getAccessToken();
      const mapping = await getChargingSiteThirdPartySystemMapping(
        id,
        accessToken
      );

      const combinedMappings = [
        ...(mapping ?? []),
        ...(thirdPartySystemMapping ?? []),
      ];

      const uniqueMappings = [];
      const seen = new Set();
      for (const m of combinedMappings) {
        const key = `${m.thirdPartySystemName}-${m.thirdPartySystemExternalId}`;
        if (!seen.has(key)) {
          uniqueMappings.push(m);
          seen.add(key);
        }
      }

      setLocalMappings(uniqueMappings);
    } catch (error) {
      console.error("Error fetching third-party system mappings:", error);
    }
  };

  const handleGetAllThirdPartySystems = async () => {
    setSystemsLoading(true);
    try {
      const accessToken = await getAccessToken();
      const systems = await getAllThirdPartySystems(accessToken);
      setThirdPartySystems(systems);
    } catch (error) {
      toast.error(t("toast.failedFetch", { entity: t("entity.thirdPartySystems") }), error);
    } finally {
      setSystemsLoading(false);
    }
  };

  const handleAddTPRef = (system, id) => {
    setHasChanges(true);
    const isExisting = localMappings.find(
      (s) =>
        s.thirdPartySystemName === system &&
        s.thirdPartySystemExternalId === id &&
        !s.isDeleted
    );

    if (isExisting && !isExisting.isDeleted) {
      toast.error(t("toast.alreadyExists", { entity: t("entity.thirdPartyReference") }));
    } else if (isExisting && isExisting.isDeleted) {
      // "Återställ" lokalt
      setLocalMappings((s) =>
        s.map((m) =>
          m.thirdPartySystemName === system &&
          m.thirdPartySystemExternalId === id
            ? { ...m, isDeleted: false }
            : m
        )
      );
      // Skicka upp diff: ta bort ev. 'isDeleted' från förälderns change-lista:
      setThirdPartySystemMapping((prev = []) =>
        prev
          .map((m) =>
            m.thirdPartySystemName === system &&
            m.thirdPartySystemExternalId === id
              ? { ...m, isDeleted: false }
              : m
          )
          .filter(
            (m) =>
              !(
                m.thirdPartySystemName === system &&
                m.thirdPartySystemExternalId === id &&
                m.isNew !== true &&
                m.isDeleted === false
              )
          )
      );
      setAddModal(false);
    } else {
      const thirdPartySystem = thirdPartySystems.find((s) => s.name === system);
      setLocalMappings((s) => [
        ...s,
        {
          thirdPartySystemName: system,
          thirdPartySystemExternalId: id,
          thirdPartySystemType: thirdPartySystem?.type ?? "",
          thirdPartySystemPublicId:
            thirdPartySystem?.thirdPartySystemPublicId ?? "",
          isNew: true,
        },
      ]);
      setThirdPartySystemMapping((s = []) => [
        ...s,
        {
          thirdPartySystemName: system,
          thirdPartySystemExternalId: id,
          thirdPartySystemType: thirdPartySystem?.type ?? "",
          thirdPartySystemPublicId:
            thirdPartySystem?.thirdPartySystemPublicId ?? "",
          chargingSitePublicId: id,
          isNew: true,
        },
      ]);
      setAddModal(false);
    }
  };

  const handleRemoveTPRef = (system, id) => {
    setHasChanges(true);
    setLocalMappings((prev) => {
      const target = prev.find(
        (m) =>
          m.thirdPartySystemName === system &&
          m.thirdPartySystemExternalId === id &&
          !m.isDeleted
      );
      if (!target) return prev;

      // Om posten är ny (isNew), ta bort den helt lokalt och ur förälderns diff-lista
      if (target.isNew) {
        setThirdPartySystemMapping((changes = []) =>
          changes.filter(
            (m) =>
              !(
                m.thirdPartySystemName === system &&
                m.thirdPartySystemExternalId === id &&
                m.isNew
              )
          )
        );
        return prev.filter(
          (m) =>
            !(
              m.thirdPartySystemName === system &&
              m.thirdPartySystemExternalId === id
            )
        );
      }

      // Annars: markera lokalt som isDeleted
      const updatedLocal = prev.map((m) =>
        m.thirdPartySystemName === system && m.thirdPartySystemExternalId === id
          ? { ...m, isDeleted: true }
          : m
      );

      // Skicka upp diff: lägg/uppdatera en isDeleted-post till förälder
      setThirdPartySystemMapping((changes = []) => {
        const idx = changes.findIndex(
          (m) =>
            m.thirdPartySystemName === system &&
            m.thirdPartySystemExternalId === id
        );
        const base = {
          thirdPartySystemName: target.thirdPartySystemName,
          thirdPartySystemExternalId: target.thirdPartySystemExternalId,
          thirdPartySystemType: target.thirdPartySystemType,
          thirdPartySystemPublicId: target.thirdPartySystemPublicId,
          chargingSitePublicId: id,
        };
        if (idx >= 0) {
          const clone = [...changes];
          clone[idx] = {
            ...clone[idx],
            ...base,
            isNew: false,
            isDeleted: true,
          };
          return clone;
        }
        return [...changes, { ...base, isNew: false, isDeleted: true }];
      });

      return updatedLocal;
    });
  };

  return (
    <>
      <ThirdPartySystemTable
        mappings={localMappings}
        thirdPartySystems={thirdPartySystems}
        systemsLoading={systemsLoading}
        addModal={addModal}
        setAddModal={setAddModal}
        addTPRState={addTPRState}
        setAddTPRState={setAddTPRState}
        addTPRef={handleAddTPRef}
        removeTPRef={handleRemoveTPRef}
        setConfirmState={setConfirmState}
      />

      {/* Modals */}
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
        onConfirm={() => {
          setConfirmState({ ...confirmState, isOpen: false });
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default SiteThirdPartyRef;

const ThirdPartySystemModal = ({
  addModal,
  setAddModal,
  thirdPartySystems,
  systemsLoading,
  addTPRState,
  setAddTPRState,
  addTPRef,
}) => {
  const { t } = useTranslation();
  return (
    <ModalBase isOpen={addModal} onClose={() => setAddModal(false)} className="!overflow-visible">
      <div className="flex flex-col justify-start gap-4 items-start">
        <h3>{t("thirdParty.reference")}</h3>

        {systemsLoading ? (
          <div className="flex w-full justify-center py-6">
            <LoadingSpinner text={t("thirdParty.loadingSystems")} delayMs={0} />
          </div>
        ) : (
          <Dropdown
            label={t("entity.system")}
            options={[
              ...thirdPartySystems.map((s) => {
                return s.name;
              }),
            ]}
            onSelect={(e) => {
              setAddTPRState((s) => ({ ...s, system: e }));
            }}
            selectedOptions={[addTPRState.system]}
            managedSelections={true}
          ></Dropdown>
        )}

        <InputField
          label={t("thirdParty.siteId")}
          placeholder={t("table.siteId")}
          value={addTPRState.id}
          onChange={(e) => {
            setAddTPRState((s) => ({ ...s, id: e.target?.value }));
          }}
        ></InputField>

        <div className="flex gap-2 w-full justify-end">
          <button
            className="btn btn-outline-primary"
            onClick={() => setAddModal(false)}
          >
            {t("common.cancel")}
          </button>

          <button
            className="btn btn-primary"
            disabled={!addTPRState.id}
            onClick={() => {
              addTPRef(addTPRState.system, addTPRState.id);
            }}
          >
            {t("common.add")}
          </button>
        </div>
      </div>
    </ModalBase>
  );
};

const ThirdPartySystemTable = ({
  addModal,
  setAddModal,
  thirdPartySystems,
  addTPRState,
  setAddTPRState,
  addTPRef,
  mappings,
  setConfirmState,
  removeTPRef,
  systemsLoading,
}) => {
  const { t } = useTranslation();
  const [filteredMappings, setFilteredMappings] = useState(
    mappings
  );
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");

  useEffect(() => {
    applyFilterAndSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [search, mappings, sortColumn, sortOrder]);

  const applyFilterAndSort = () => {
   let filtered = (mappings ?? []).filter((i) => !i.isDeleted);

    // Filter based on search
    if (search) {
      const searchLower = search?.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.thirdPartySystemName?.toLowerCase().includes(searchLower) ||
          item.thirdPartySystemType?.toLowerCase().includes(searchLower) ||
          item.thirdPartySystemExternalId?.toLowerCase().includes(searchLower)
      );
    }

    // Sort based on selected column
    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[sortColumn]?.toString()?.toLowerCase();
      let bValue = b[sortColumn]?.toString()?.toLowerCase();

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredMappings(sorted);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === "asc" ? faArrowDownShortWide : faArrowDownWideShort;
    }
    return null;
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-row justify-between items-center">
        <h4 className="mb-2">{t("thirdParty.references")}</h4>
        <div className="flex justify-end">
          <div className="mb-2">
            <InputField
              icon={faSearch}
              placeholder={t("thirdParty.searchByIdOrName")}
              value={search}
              onChange={(v) => {
                setSearch(v.target.value);
              }}
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto pb-[250px] mb-[-250px]">
        <table className="table-default w-full">
          <thead>
            <tr>
              <th
                className="cursor-pointer"
                onClick={() => handleSort("thirdPartySystemName")}
              >
                {t("entity.system")}{" "}
                {getSortIcon("thirdPartySystemName") && (
                  <FontAwesomeIcon
                    icon={getSortIcon("thirdPartySystemName")}
                    className="w-4 pl-2"
                  />
                )}
              </th>
              <th
                className="cursor-pointer"
                onClick={() => handleSort("thirdPartySystemType")}
              >
                {t("common.type")}{" "}
                {getSortIcon("thirdPartySystemType") && (
                  <FontAwesomeIcon
                    icon={getSortIcon("thirdPartySystemType")}
                    className="w-4 pl-2"
                  />
                )}
              </th>
              <th
                className="cursor-pointer"
                onClick={() => handleSort("thirdPartySystemExternalId")}
              >
                {t("thirdParty.siteId")}{" "}
                {getSortIcon("thirdPartySystemExternalId") && (
                  <FontAwesomeIcon
                    icon={getSortIcon("thirdPartySystemExternalId")}
                    className="w-4 pl-2"
                  />
                )}
              </th>
              <th>
                <div className="flex w-full justify-end items-center">
                  <button
                    className="block text-sm text-primary-600 hover:text-primary-400 text-left rounded-lg"
                    onClick={() => {
                      setAddModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> {t("common.addNew")}
                  </button>
                  <ThirdPartySystemModal
                    addModal={addModal}
                    setAddModal={setAddModal}
                    thirdPartySystems={thirdPartySystems}
                    systemsLoading={systemsLoading}
                    addTPRState={addTPRState}
                    setAddTPRState={setAddTPRState}
                    addTPRef={addTPRef}
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredMappings.map((tbsm, index) => (
              <tr key={index}>
                <td>{tbsm.thirdPartySystemName}</td>
                <td>{tbsm.thirdPartySystemType}</td>
                <td>{tbsm.thirdPartySystemExternalId}</td>
                <td className="text-center flex justify-end">
                  <CustomDropdown
                    isRight={true}
                    ContentComponent={() => (
                      <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                        <button
                          className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                          onClick={() => {
                            setConfirmState((s) => ({
                              ...s,
                              isOpen: true,
                              id: tbsm.thirdPartySystemExternalId,
                              title: t("thirdParty.deleteWithId", { id: tbsm.thirdPartySystemExternalId }),
                              confirm: () => {
                                removeTPRef(
                                  tbsm.thirdPartySystemName,
                                  tbsm.thirdPartySystemExternalId
                                );
                              },
                            }));
                          }}
                        >
                          <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                        </button>
                      </div>
                    )}
                  >
                    <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                      <FontAwesomeIcon icon={faEllipsis} />
                    </div>
                  </CustomDropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
